<script lang="ts" setup>
import { i18n } from '@/i18n';
import { UPDATE_TO_APPROVED_MENU_ITEMS } from '../constants';
import { nextTick, onMounted, onUnmounted, ref } from 'vue';
import { UpdateToApprovedMenuItems } from '../entities/updateToApprovedMenu';
import { UpdateToApprovedMenuService } from '../service/updateToApprovedMenuService';
import { watch } from 'vue';
import SvgLoader from '@/utils/SvgLoader.vue';

const props = defineProps<{
  disabled?: boolean;
}>();

const t = i18n.t;

const isOpen = ref(false);

const OPTION_HEIGHT = 40;

const emit = defineEmits<{
  (e: 'selectedItem', item: UpdateToApprovedMenuItems): void;
}>();

watch(isOpen, async (newVal) => {
  if (newVal) {
    calculateDropdownPosition();
  }
});

/**
 * ドロップダウンの表示状態を切り替えます。
 */
const toggleDropdown = () => {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
};

/**
 * オプションを選択し、親コンポーネントに選択値を通知します。
 * @param item - 選択したアイテム
 */
const selectOption = (item: UpdateToApprovedMenuItems) => {
  emit('selectedItem', item);
  isOpen.value = false;
};

/**
 * ドロップダウンを閉じます。
 * @param event - クリックイベント
 */
const closeDropdown = (event: MouseEvent) => {
  if (!isOpen.value) return;
  const target = event.target;
  const selector = '#update-to-approved-menu-dropdown-container';
  const arrowSelector = '#update-to-approved-menu-dropdown-arrow';
  const searchSelector = '#update-to-approved-menu-dropdown-content';
  if (!(target instanceof Element)) return;
  if (target.closest(arrowSelector)) return;
  if (target.closest(selector)) return;
  if (target.closest(searchSelector)) return;

  isOpen.value = false;
};

onMounted(() => {
  document.addEventListener('click', closeDropdown);
});

onUnmounted(() => {
  document.removeEventListener('click', closeDropdown);
});

/**
 * ドロップダウンの位置を計算し、設定します。
 */
const calculateDropdownPosition = () => {
  nextTick(() => {
    const selectBox = document.querySelector('#update-to-approved-menu-dropdown-container') as HTMLElement;
    if (selectBox) {
      const rect = selectBox.getBoundingClientRect();
      const dropdown = document.querySelector('#update-to-approved-menu-dropdown-content') as HTMLElement;
      if (dropdown) {
        dropdown.style.left = `${rect.left}px`;
        dropdown.style.top = `${rect.bottom}px`;

        // ドロップダウンの高さを計算
        const desiredHeight = OPTION_HEIGHT * UPDATE_TO_APPROVED_MENU_ITEMS.length;

        // max-heightを計算して設定
        const availableHeight = window.innerHeight - rect.bottom - 10; // 10pxは少しのマージン
        dropdown.style.maxHeight = `${Math.min(desiredHeight, availableHeight)}px`;
      }
    }
  });
};
</script>

<template>
  <div class="update-to-approved-menu_container" @click="closeDropdown($event)">
    <div id="update-to-approved-menu-dropdown-container" class="update-to-approved-menu">
      <div
        class="update-to-approved-menu__selected"
        :class="{ 'update-to-approved-menu__selected--disabled': disabled }"
        @click="toggleDropdown"
      >
        <div class="update-to-approved-menu__field">
          {{ t('ステータス変更') }}
        </div>
        <SvgLoader v-if="isOpen" name="arrowUpBlue" class="update-to-approved-menu__arrow" />
        <SvgLoader v-else id="update-to-approved-menu-dropdown-arrow" name="arrowDownBlue" class="update-to-approved-menu__arrow" />
      </div>
      <!-- TODO: 横スクロールした際にドロップダウンが追従しない -->
      <Teleport v-if="isOpen" to="body">
        <div id="update-to-approved-menu-dropdown-content" class="update-to-approved-menu__dropdown">
          <div
            v-for="option in UPDATE_TO_APPROVED_MENU_ITEMS"
            :key="option"
            class="update-to-approved-menu__option"
            @click="selectOption(option)"
          >
            {{ UpdateToApprovedMenuService.getDisplayText(option) }}
          </div>
        </div>
      </Teleport>
    </div>
  </div>
</template>

<style scoped lang="scss">
.update-to-approved-menu {
  justify-items: center;
  position: relative;
  user-select: none;
  box-sizing: border-box;
  font-family: Noto Sans JP;

  &__field {
    height: 20px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0em;
    color: $semanticPrimary;
    background-color: $semanticWhite;
  }

  &__selected {
    width: 100%;
    height: 30px;
    padding: 2px 15px;
    display: grid;
    grid-template-columns: 1fr 24px;
    text-align: center;
    align-items: center;
    cursor: pointer;
    background-color: $semanticWhite;
    border: 1px solid $semanticPrimary;
    border-radius: 4px;

    &--disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__arrow {
    display: grid;
    align-content: center;
    justify-content: center;
    height: 24px;
    width: 24px;
  }

  &__dropdown {
    position: fixed;
    background-color: $semanticWhite;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    z-index: 1050; // モーダルより高い値に設定
    overflow-y: auto;
    max-height: 300px; // 適切な最大高さを設定
    box-sizing: border-box;
  }

  &__option {
    padding: 10px;
    cursor: pointer;

    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0em;
    text-align: left;
    color: $textDefault;

    min-height: 32px;

    &:hover,
    &:focus {
      background-color: $semanticPrimarySubdued;
    }

    &--disabled {
      color: #ccc;
      cursor: not-allowed;
    }
  }
}
</style>
